import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, Building2, Lock, Mail, ShieldCheck, UserRound } from 'lucide-react';
import { LinkedInIcon } from '@/assets/icons/LinkedInIcon';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { ROUTES } from '@/lib/constants/routes';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { TermsModal } from '@/components/common/TermsModal';
import { TextField } from '@/components/forms/TextField';
import { CheckboxField } from '@/components/forms/CheckboxField';
import { AuthHero } from '@/components/common/AuthHero';
import { SectionCard } from '@/components/common/SectionCard';
import { ShieldNote } from '@/components/common/ShieldNote';
import { useInvite } from '@/hooks/useInvite';
import { useAcceptInvite } from '@/hooks/useAcceptInvite';
import { useAuth } from '@/hooks/useAuth';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { getApiErrorCode } from '@/lib/utils/error.utils';
import { InviteExpiredState } from './InviteExpiredState';
import { InviteAcceptConfirmCard } from './InviteAcceptConfirmCard';
import { InviteWrongAccountState } from './InviteWrongAccountState';
import { markStartupInviteLanding } from '@/lib/auth/startup-invite-landing';
import type { InvitePreview } from '@/api/invites.api';

// Full name, password and LinkedIn are the fields the backend accept-invite
// contract accepts (US-038/US-098). Company Name is read-only, sourced from
// the invite's entityName - the invitee is joining that specific entity, not
// naming their own company, so it isn't part of the form values at all.
const acceptInviteSchema = z
  .object({
    fullName: z.string().trim().min(1, 'Full name is required'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    linkedinUrl: z.union([z.string().trim().url('Enter a valid URL'), z.literal('')]).optional(),
    // Unchecked (RHF reports an unchecked native checkbox as `false`, never
    // `undefined`) or stripped from the submitted values entirely both fail
    // the same way - z.literal(true) only accepts the literal `true`.
    tosAccepted: z.literal(true),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type AcceptInviteFormValues = z.infer<typeof acceptInviteSchema>;

const ICON_SIZE = 16;

/**
 * Lands from an invite link (US-038/US-098, step 1 of 3). Validates the
 * token first; an invalid/expired/already-accepted/revoked token renders the
 * dedicated expired state in place (AC3) rather than a separate route, since
 * it's only known once the token has been checked against the backend.
 *
 * `preview.acceptMethod` then branches the rest of the page:
 * - `'otp'` (no account yet, unverified, or already a member elsewhere):
 *   today's password-form flow below, handing off to `InviteVerifyOtpPage`
 *   (step 2) - no session is issued from this page.
 * - `'login'` (existing, verified, not yet a member anywhere): no password
 *   form at all - log in if needed (redirect with `?returnTo=` back here),
 *   then a lightweight accept-confirm card, no OTP.
 */
/**
 * Copy that depends on what the invite does: join an existing team, or (a
 * startup admin invite, MYN-311) create a startup that has no name yet.
 */
function signupCopy(preview: InvitePreview): { subtitle: string; showCompanyName: boolean } {
  if (preview.inviteType === 'startup_admin') {
    return { subtitle: UI_STRINGS.INVITE.STARTUP_ADMIN_SUBTITLE, showCompanyName: false };
  }
  return { subtitle: `You're joining as a team member of ${preview.entityName}.`, showCompanyName: true };
}

export function InviteSignupPage() {
  const { token = '' } = useParams<{ token: string }>();
  const invite = useInvite(token);
  const acceptInvite = useAcceptInvite(token);
  const auth = useAuth();
  const I = UI_STRINGS.INVITE;
  const [termsOpen, setTermsOpen] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<AcceptInviteFormValues>({ resolver: zodResolver(acceptInviteSchema) });

  // Pre-fills Full Name (still editable - not registered as readOnly/disabled)
  // from the name the inviter supplied at invite-creation time, if any.
  // setValue rather than defaultValues/reset: `invite.data` isn't available
  // until this query resolves, well after the initial useForm() call, and a
  // reset() here would also clobber password/confirmPassword/linkedinUrl if
  // this effect ever re-ran after the invitee started typing.
  useEffect(() => {
    if (invite.data?.inviteeName) {
      setValue('fullName', invite.data.inviteeName);
    }
  }, [invite.data, setValue]);

  // A verified account already exists for the invited email (409): the password
  // form cannot accept it, the signed-in path can. Sign in, then come back here,
  // where the invite is previewed as `acceptMethod: 'login'`.
  const hasExistingAccount =
    acceptInvite.isError && getApiErrorCode(acceptInvite.error) === API_ERROR_CODES.EMAIL_ALREADY_REGISTERED;
  const signInToAcceptPath = `${ROUTES.AUTH.LOGIN}?returnTo=${encodeURIComponent(
    ROUTES.AUTH.INVITE_SIGNUP.replace(':token', token),
  )}`;

  const onSubmit = ({ fullName, password, linkedinUrl, tosAccepted }: AcceptInviteFormValues) => {
    // A startup admin invitee creates their startup right after signing in (MYN-311).
    if (invite.data?.inviteType === 'startup_admin') markStartupInviteLanding();
    acceptInvite.mutate({ fullName, password, linkedinUrl: linkedinUrl || undefined, tosAccepted });
  };

  if (invite.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" label="Checking your invite" />
      </div>
    );
  }

  if (invite.isError) {
    return <InviteExpiredState errorCode={getApiErrorCode(invite.error)} />;
  }

  const preview = invite.data;

  if (preview.acceptMethod === 'login') {
    if (auth.isLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" label="Checking your session" />
        </div>
      );
    }
    if (!auth.isAuthenticated) {
      const inviteSignupPath = ROUTES.AUTH.INVITE_SIGNUP.replace(':token', token);
      return (
        <Navigate
          to={`${ROUTES.AUTH.LOGIN}?returnTo=${encodeURIComponent(inviteSignupPath)}`}
          replace
        />
      );
    }
    if (auth.user?.email.trim().toLowerCase() !== preview.recipientEmail.trim().toLowerCase()) {
      return <InviteWrongAccountState recipientEmail={preview.recipientEmail} />;
    }
    return <InviteAcceptConfirmCard token={token} entityName={preview.entityName} />;
  }

  return (
    <div className="flex flex-col items-center bg-page-bg px-4 py-16">
      <AuthHero
        badgeIcon={<ShieldCheck size={14} />}
        badgeText={I.BADGE}
        heading={I.HEADING}
        subtitle={signupCopy(preview).subtitle}
      />

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="mt-10 w-full max-w-3xl">
        <SectionCard title={I.CARD_TITLE} subtitle={I.CARD_SUBTITLE} icon={<UserRound size={20} />}>
          {acceptInvite.isError && (
            <div role="alert" className="mb-6 flex flex-col items-center gap-3 text-center text-sm text-danger">
              <p>{hasExistingAccount ? I.ERROR_EXISTING_ACCOUNT : I.ERROR}</p>
              {hasExistingAccount && (
                <Button asChild variant="navy">
                  <Link to={signInToAcceptPath}>{I.SIGN_IN_TO_ACCEPT}</Link>
                </Button>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            <TextField
              {...register('fullName')}
              label={I.FULL_NAME_LABEL}
              required
              type="text"
              autoComplete="name"
              placeholder={I.FULL_NAME_PLACEHOLDER}
              icon={<UserRound size={ICON_SIZE} />}
              error={errors.fullName?.message}
            />
            <TextField
              label={I.EMAIL_LABEL}
              required
              type="email"
              value={preview.recipientEmail}
              disabled
              icon={<Mail size={ICON_SIZE} />}
            />
            <TextField
              {...register('password')}
              label={I.PASSWORD_LABEL}
              required
              type="password"
              autoComplete="new-password"
              placeholder={I.PASSWORD_PLACEHOLDER}
              icon={<Lock size={ICON_SIZE} />}
              error={errors.password?.message}
            />
            <TextField
              {...register('confirmPassword')}
              label={I.CONFIRM_PASSWORD_LABEL}
              required
              type="password"
              autoComplete="new-password"
              placeholder={I.PASSWORD_PLACEHOLDER}
              icon={<Lock size={ICON_SIZE} />}
              error={errors.confirmPassword?.message}
            />
            {signupCopy(preview).showCompanyName && (
              <TextField
                label={I.COMPANY_NAME_LABEL}
                type="text"
                value={preview.entityName}
                disabled
                icon={<Building2 size={ICON_SIZE} />}
              />
            )}
            <TextField
              {...register('linkedinUrl')}
              label={`${I.LINKEDIN_LABEL} ${I.LINKEDIN_LABEL_OPTIONAL}`}
              type="url"
              placeholder={I.LINKEDIN_PLACEHOLDER}
              icon={<LinkedInIcon size={ICON_SIZE} />}
              error={errors.linkedinUrl?.message}
            />
          </div>
        </SectionCard>

        <div className="mt-6 flex flex-col items-center gap-5">
          <CheckboxField
            {...register('tosAccepted')}
            label={
              <>
                {I.CONSENT_LABEL_PREFIX}
                <Button
                  type="button"
                  variant="link"
                  onClick={(event) => {
                    // Nested inside the checkbox's <label> - without this the
                    // native label-forwards-click-to-control behaviour would
                    // also toggle the checkbox when this link is clicked.
                    event.stopPropagation();
                    setTermsOpen(true);
                  }}
                  className="underline"
                >
                  {I.CONSENT_LABEL_LINK}
                </Button>
                {I.CONSENT_LABEL_SUFFIX}
              </>
            }
            error={errors.tosAccepted ? I.CONSENT_REQUIRED_ERROR : undefined}
          />
          <ShieldNote>{I.ENCRYPTION_NOTE}</ShieldNote>
          <Button
            type="submit"
            variant="navy"
            disabled={acceptInvite.isPending}
            className="inline-flex items-center gap-2 px-10 py-3.5 text-base drop-shadow-md"
          >
            {acceptInvite.isPending ? I.SUBMITTING : I.SUBMIT}
            <ArrowRight size={18} aria-hidden="true" />
          </Button>
        </div>
      </form>

      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    </div>
  );
}
